import { useState, useEffect } from 'react';

interface NetworkSpeedResult {
  isSlowConnection: boolean;
  connectionType: string | null;
  effectiveType: string | null;
}

/**
 * Hook to detect slow network connections
 * Uses the Network Information API when available
 */
export function useNetworkSpeed(): NetworkSpeedResult {
  const [isSlowConnection, setIsSlowConnection] = useState(false);
  const [connectionType, setConnectionType] = useState<string | null>(null);
  const [effectiveType, setEffectiveType] = useState<string | null>(null);

  useEffect(() => {
    // Check if the Network Information API is available
    const connection = (navigator as any).connection || 
                      (navigator as any).mozConnection || 
                      (navigator as any).webkitConnection;

    if (!connection) {
      // Network Information API not available, assume good connection
      return;
    }

    const updateConnectionInfo = () => {
      const effective = connection.effectiveType;
      const type = connection.type;
      
      setConnectionType(type || null);
      setEffectiveType(effective || null);

      // Consider connection slow if:
      // - effectiveType is 'slow-2g' or '2g'
      // - downlink is very low (< 1 Mbps)
      // - rtt (round-trip time) is very high (> 500ms)
      const isSlow = 
        effective === 'slow-2g' || 
        effective === '2g' ||
        (connection.downlink && connection.downlink < 1) ||
        (connection.rtt && connection.rtt > 500);

      setIsSlowConnection(isSlow);
    };

    // Initial check
    updateConnectionInfo();

    // Listen for connection changes
    connection.addEventListener('change', updateConnectionInfo);

    return () => {
      connection.removeEventListener('change', updateConnectionInfo);
    };
  }, []);

  return { isSlowConnection, connectionType, effectiveType };
}
